<script setup>
import { computed } from 'vue';
import LeadCard from './LeadCard.vue';

const props = defineProps({
  stage: { type: Object, required: true },
  stageIndex: { type: Number, default: 0 },
  stageCount: { type: Number, default: 0 },
  leads: { type: Array, default: () => [] },
  isDragOver: { type: Boolean, default: false },
});

// Rótulo do tipo de estágio em pt-BR (era exibido cru: OPEN/WON/LOST).
const KIND_LABELS = {
  open: 'Aberto',
  won: 'Ganho',
  lost: 'Perdido',
};
const kindLabel = computed(
  () => KIND_LABELS[props.stage.kind] || props.stage.kind
);

const emit = defineEmits([
  'dragover',
  'dragleave',
  'drop',
  'leadClick',
  'leadDragStart',
  'leadDragEnd',
]);

// Soma do valor dos leads da coluna (formatada) - métrica de header.
const totalValue = computed(() => {
  const cents = props.leads.reduce((s, l) => s + (l.value_cents || 0), 0);
  if (cents <= 0) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(cents / 100);
});

// Leads parados (>14d sem atividade) - alerta na coluna.
const staleCount = computed(
  () =>
    props.leads.filter(l => {
      if (!l.last_activity_at) return false;
      return (
        (Date.now() - new Date(l.last_activity_at).getTime()) / 86400000 > 14
      );
    }).length
);

const kindChip = computed(() => {
  if (props.stage.kind === 'won') return 'bg-n-grass-3 text-n-grass-11';
  if (props.stage.kind === 'lost') return 'bg-n-ruby-3 text-n-ruby-11';
  return 'bg-n-alpha-2 text-n-slate-11';
});
</script>

<template>
  <div
    class="flex-shrink-0 w-80 flex flex-col rounded-xl border bg-n-alpha-1 transition-all"
    :class="
      isDragOver
        ? 'border-n-brand ring-1 ring-n-brand bg-n-alpha-2'
        : 'border-n-weak'
    "
    @dragover="emit('dragover', $event)"
    @dragleave="emit('dragleave', $event)"
    @drop="emit('drop', $event)"
  >
    <!-- Header: cor + nome + contador (pill) + tipo, e linha de métricas
         agregadas (valor · parados). -->
    <div class="px-3 py-2.5 border-b border-n-weak">
      <div class="flex items-center justify-between gap-2 min-w-0">
        <div class="flex items-center gap-2 min-w-0">
          <span
            class="size-2.5 rounded-full shrink-0"
            :style="{ background: stage.color }"
          />
          <span class="text-heading-3 text-n-slate-12 truncate">
            {{ stage.name }}
          </span>
          <span
            class="shrink-0 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-label-small font-semibold bg-n-alpha-2 text-n-slate-11"
          >
            {{ leads.length }}
          </span>
        </div>
        <span
          class="shrink-0 text-label-small px-1.5 py-0.5 rounded uppercase font-medium"
          :class="kindChip"
        >
          {{ kindLabel }}
        </span>
      </div>
      <div
        v-if="totalValue || staleCount"
        class="flex items-center gap-3 mt-1.5 text-label-small text-n-slate-11"
      >
        <span
          v-if="totalValue"
          class="inline-flex items-center gap-1 text-n-slate-12 font-medium"
        >
          <i class="i-lucide-banknote size-3" />
          {{ totalValue }}
        </span>
        <span
          v-if="staleCount"
          class="inline-flex items-center gap-1 text-n-amber-11"
        >
          <i class="i-lucide-alert-triangle size-3" />
          {{ staleCount }} parado(s)
        </span>
      </div>
    </div>

    <div class="flex-1 p-2 pb-3 flex flex-col gap-2">
      <LeadCard
        v-for="lead in leads"
        :key="lead.id"
        :lead="lead"
        :stage-index="stageIndex"
        :stage-count="stageCount"
        @dragstart="emit('leadDragStart', { lead, event: $event })"
        @dragend="emit('leadDragEnd', $event)"
        @click="emit('leadClick', lead)"
      />
      <div
        v-if="!leads.length"
        class="flex flex-col items-center justify-center gap-1.5 py-10 text-center"
      >
        <i class="i-lucide-inbox size-6 text-n-slate-9" />
        <span class="text-body-small text-n-slate-10">Sem leads aqui</span>
      </div>
    </div>
  </div>
</template>
